/**
 * DISPLAY SETTINGS, the reader's Aa (2026-09-13, the Kindle layer).
 *
 * What a Kindle does that a news site does not: it treats the page as the reader's, not the
 * publisher's. One control, "Aa", holds the theme, the typeface, the size, the boldness, the
 * line spacing, the margins and the alignment, and whatever the reader picks holds across every
 * book. This module is that control's state: one object, one storage key, one function that
 * paints it onto <html> as data attributes and custom properties so every sheet can read it.
 *
 * The typeface list is deliberately three long. Kindle's own is Bookerly (a book face drawn for
 * screens), Amazon Ember (a plain sans) and a handful of classics. Ours: Literata, the open book
 * face Google drew for Play Books with the same brief as Bookerly and an optical-size axis so
 * small text gets sturdier cuts; Newsreader, the face the headline already wears; and Inter,
 * the plain sans for a tired eye. Three distinct voices, not nine near-identical ones.
 *
 * Pure: no DOM at module scope, so it is testable and safe to import from anywhere.
 * House style: no em dashes or en dashes in any string here.
 */

export type DisplayFace = "book" | "news" | "plain";
export type DisplayAlign = "left" | "justify";

export interface Display {
  face: DisplayFace;
  /** 1..7, Kindle's slider cut to the seven steps a body size can actually use. 4 is the type scale as designed. */
  size: number;
  /** 0 regular, 1 medium, 2 bold. Kindle calls this "boldness". */
  weight: 0 | 1 | 2;
  /** 0 tight, 1 normal, 2 loose line spacing. */
  leading: 0 | 1 | 2;
  align: DisplayAlign;
  /** 0..4, the Paperwhite's warm light: an amber veil over the whole page, multiplied in. */
  warmth: number;
}

export const DISPLAY_KEY = "insnip.display";
/** The key the three-step Text size control wrote before this (sm / md / lg). Read once, then superseded. */
export const LEGACY_SIZE_KEY = "insnip.readingSize";

export const SIZE_MIN = 1;
export const SIZE_MAX = 7;
export const WARMTH_MAX = 4;

export const DISPLAY_DEFAULTS: Display = {
  // Newsreader by default since 2026-09-18 (USER: pointers "should not be in bold ... soothe
  // eyes"); Literata's sturdy text cut read as bold on the dark ground. See kindle.css 0.
  face: "news",
  size: 4,
  weight: 0,
  leading: 1,
  align: "left",
  warmth: 0,
};

export interface FaceOption {
  id: DisplayFace;
  label: string;
  /** One line under the name in the sheet. */
  note: string;
}

export const FACES: readonly FaceOption[] = [
  { id: "news", label: "Newsreader", note: "Light and clear, drawn for news" },
  { id: "book", label: "Literata", note: "A sturdier book face drawn for screens" },
  { id: "plain", label: "Inter", note: "Plain sans for a tired eye" },
];

/** Multipliers on the designed type scale, one per size step. Step 4 is 1. */
export const SIZE_SCALE: readonly number[] = [0.84, 0.91, 0.96, 1, 1.07, 1.16, 1.27];
/** CSS font-weight per boldness step. Literata and Newsreader are variable; Inter ships 400/500/600. */
export const WEIGHT_VALUES: readonly number[] = [400, 500, 600];
/** Unitless line-height per spacing step. */
export const LEADING_VALUES: readonly number[] = [1.46, 1.62, 1.82];
/** Veil opacity per warmth step. Multiplied amber over white reads as a warm page; over black it tints the type. */
export const WARMTH_OPACITY: readonly number[] = [0, 0.07, 0.13, 0.2, 0.28];

export const WEIGHT_LABELS = ["Regular", "Medium", "Bold"] as const;
export const LEADING_LABELS = ["Tight", "Normal", "Loose"] as const;

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Accept anything a previous build, a hand edit or a test may have stored and return a valid Display. */
export function normalizeDisplay(raw: unknown): Display {
  const r = (raw && typeof raw === "object" ? raw : {}) as Partial<Record<keyof Display, unknown>>;
  const face = FACES.some((f) => f.id === r.face) ? (r.face as DisplayFace) : DISPLAY_DEFAULTS.face;
  const align: DisplayAlign = r.align === "justify" ? "justify" : "left";
  return {
    face,
    size: clampInt(r.size, SIZE_MIN, SIZE_MAX, DISPLAY_DEFAULTS.size),
    weight: clampInt(r.weight, 0, 2, DISPLAY_DEFAULTS.weight) as 0 | 1 | 2,
    leading: clampInt(r.leading, 0, 2, DISPLAY_DEFAULTS.leading) as 0 | 1 | 2,
    align,
    warmth: clampInt(r.warmth, 0, WARMTH_MAX, DISPLAY_DEFAULTS.warmth),
  };
}

/** The old three sizes land on the new seven-step slider: small 2, medium 4, large 6. */
export function migrateReadingSize(stored: string | null | undefined): number | null {
  if (!stored) return null;
  let v: unknown = stored;
  try {
    v = JSON.parse(stored);
  } catch {
    /* a bare word, not JSON: read it as-is */
  }
  if (v === "sm") return 2;
  if (v === "md") return 4;
  if (v === "lg") return 6;
  return null;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function storageOf(storage?: StorageLike): StorageLike | null {
  if (storage) return storage;
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** Read the stored display, or build one from the legacy size key, or the defaults. Never throws. */
export function loadDisplay(storage?: StorageLike): Display {
  const s = storageOf(storage);
  if (!s) return { ...DISPLAY_DEFAULTS };
  try {
    const raw = s.getItem(DISPLAY_KEY);
    if (raw) return normalizeDisplay(JSON.parse(raw));
  } catch {
    /* fall through to the legacy key */
  }
  let legacy: number | null = null;
  try {
    legacy = migrateReadingSize(s.getItem(LEGACY_SIZE_KEY));
  } catch {
    legacy = null;
  }
  return { ...DISPLAY_DEFAULTS, size: legacy ?? DISPLAY_DEFAULTS.size };
}

export function saveDisplay(display: Display, storage?: StorageLike): void {
  const s = storageOf(storage);
  if (!s) return;
  try {
    s.setItem(DISPLAY_KEY, JSON.stringify(normalizeDisplay(display)));
  } catch {
    /* private mode or a full store: the setting still applies for this visit */
  }
}

export function isDefaultDisplay(display: Display): boolean {
  const d = normalizeDisplay(display);
  return (Object.keys(DISPLAY_DEFAULTS) as (keyof Display)[]).every((k) => d[k] === DISPLAY_DEFAULTS[k]);
}

/** The custom properties the stylesheet reads. Exposed so a test can assert the numbers, not the DOM. */
export function displayVars(display: Display): Record<string, string> {
  const d = normalizeDisplay(display);
  return {
    "--read-scale": String(SIZE_SCALE[d.size - 1]),
    "--read-weight": String(WEIGHT_VALUES[d.weight]),
    "--read-leading": String(LEADING_VALUES[d.leading]),
    "--read-warmth": String(WARMTH_OPACITY[d.warmth]),
  };
}

interface RootLike {
  setAttribute(name: string, value: string): void;
  style: { setProperty(name: string, value: string): void };
}

/**
 * Paint the display onto <html>. `data-display` is the one switch every rule in kindle.css
 * hangs on (no attribute, no rule fires, which is how the server render and the first paint
 * stay the designed scale); the face and alignment ride as attributes so CSS can pick font
 * stacks; the numbers ride as custom properties.
 */
export function applyDisplay(root: RootLike, display: Display): void {
  const d = normalizeDisplay(display);
  root.setAttribute("data-display", "on");
  root.setAttribute("data-read-face", d.face);
  root.setAttribute("data-read-align", d.align);
  root.setAttribute("data-read-size", String(d.size));
  for (const [k, v] of Object.entries(displayVars(d))) root.style.setProperty(k, v);
}

/**
 * Kindle's footer line, "3 mins left in chapter", for a round of ten. Takes the seconds the
 * rest of the round (this story included) is estimated to take.
 */
export function roundTimeLeft(secondsLeft: number, isLast: boolean): string {
  if (isLast) return "Last story in this round";
  const s = Math.max(0, Math.round(secondsLeft));
  if (s < 60) return "Under a minute left in this round";
  const m = Math.round(s / 60);
  return `${m} min left in this round`;
}
